import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { API, cn, formatDate, DEPARTMENT_COLORS } from "../lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import { Skeleton } from "../components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Plus,
  Search,
  Package,
  Calendar,
  Trash2,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

export default function OrdersPage() {
  const { isAdmin, isSuperAdmin } = useAuth();
  const [orders, setOrders] = useState([]);
  const [orderTaskSummary, setOrderTaskSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ordersRes, summaryRes] = await Promise.all([
          axios.get(`${API}/orders`, { withCredentials: true }),
          axios.get(`${API}/dashboard/order-task-summary`, { withCredentials: true }),
        ]);
        setOrders(ordersRes.data);
        setOrderTaskSummary(summaryRes.data);
      } catch (err) {
        console.error("Failed to fetch orders:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Build a lookup for task summary by order_id
  const summaryMap = {};
  orderTaskSummary.forEach((s) => { summaryMap[s.order_id] = s; });

  const filteredOrders = orders.filter(
    (order) =>
      order.order_number.toLowerCase().includes(search.toLowerCase()) ||
      order.customer_name.toLowerCase().includes(search.toLowerCase())
  );

  const handleSearchChange = async (value) => {
    setSearch(value);
    if (value.length >= 2) {
      try {
        const res = await axios.get(`${API}/search/orders?q=${encodeURIComponent(value)}`, { withCredentials: true });
        setSearchSuggestions(res.data);
        setShowSuggestions(true);
      } catch (err) {
        setSearchSuggestions([]);
      }
    } else {
      setSearchSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (orderId) => {
    setShowSuggestions(false);
    navigate(`/orders/${orderId}`);
  };

  const handleDeleteOrder = async (e, orderId, orderNumber) => {
    e.stopPropagation();
    setDeleting(orderId);
    try {
      await axios.delete(`${API}/orders/${orderId}`, { withCredentials: true });
      toast.success(`Order "${orderNumber}" deleted successfully`);
      setOrders((prev) => prev.filter((o) => o.order_id !== orderId));
    } catch (err) {
      console.error("Failed to delete order:", err);
      toast.error(err.response?.data?.detail || "Failed to delete order");
    } finally {
      setDeleting(null);
    }
  };

  const handleRequestDeletion = async (e, orderId, orderNumber) => {
    e.stopPropagation();
    setDeleting(orderId);
    try {
      await axios.post(`${API}/deletion-requests`, {
        entity_type: "order",
        entity_id: orderId,
        entity_name: orderNumber,
      });
      toast.success("Deletion request submitted for approval");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit deletion request");
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return <OrdersSkeleton />;
  }

  return (
    <div className="space-y-6" data-testid="orders-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl tracking-tight">Orders</h1>
          <p className="text-muted-foreground mt-1">
            Manage sales orders and track progress
          </p>
        </div>
        <Link to="/orders/new">
          <Button className="rounded-sm" data-testid="new-order-button">
            <Plus className="w-4 h-4 mr-2" />
            New Order
          </Button>
        </Link>
      </div>

      {/* Search */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by order #, customer, item name/SKU/customer item #..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            onFocus={() => searchSuggestions.length > 0 && setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            className="pl-10 rounded-sm"
            data-testid="order-search"
          />
          {showSuggestions && searchSuggestions.length > 0 && (
            <div className="absolute top-full mt-1 left-0 right-0 bg-background border rounded-sm shadow-lg z-50 max-h-48 overflow-y-auto">
              {searchSuggestions.map((s) => (
                <button
                  key={s.order_id}
                  className="w-full text-left px-3 py-2 hover:bg-muted text-sm flex justify-between items-center"
                  onMouseDown={() => handleSuggestionClick(s.order_id)}
                  data-testid={`order-suggestion-${s.order_id}`}
                >
                  <span className="font-mono font-medium">{s.order_number}</span>
                  <span className="text-muted-foreground text-xs">{s.customer_name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Orders Table with Task Summary */}
      <Card className="border border-border shadow-none rounded-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="font-mono text-xs uppercase tracking-widest whitespace-nowrap">Order #</TableHead>
                  <TableHead className="font-mono text-xs uppercase tracking-widest whitespace-nowrap">Customer</TableHead>
                  <TableHead className="font-mono text-xs uppercase tracking-widest whitespace-nowrap">Progress</TableHead>
                  <TableHead className="font-mono text-xs uppercase tracking-widest whitespace-nowrap">Merchandizing</TableHead>
                  <TableHead className="font-mono text-xs uppercase tracking-widest whitespace-nowrap">Prepress</TableHead>
                  <TableHead className="font-mono text-xs uppercase tracking-widest whitespace-nowrap">Production</TableHead>
                  <TableHead className="font-mono text-xs uppercase tracking-widest whitespace-nowrap">Logistics</TableHead>
                  <TableHead className="font-mono text-xs uppercase tracking-widest whitespace-nowrap">Status</TableHead>
                  {isAdmin && (
                    <TableHead className="font-mono text-xs uppercase tracking-widest text-right whitespace-nowrap">Actions</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.map((order) => {
                  const summary = summaryMap[order.order_id];
                  const progress = summary ? summary.progress : (order.total_tasks > 0 ? Math.round(order.completed_tasks / order.total_tasks * 100) : 0);
                  return (
                    <TableRow
                      key={order.order_id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/orders/${order.order_id}`)}
                      data-testid={`order-row-${order.order_id}`}
                    >
                      <TableCell className="font-mono font-medium whitespace-nowrap">
                        {order.order_number}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">{order.customer_name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 min-w-[100px]">
                          <Progress value={progress} className="h-1.5 flex-1" />
                          <span className="text-xs font-mono font-medium">{progress}%</span>
                        </div>
                      </TableCell>
                      {["merchandizing", "prepress", "production", "logistics"].map((dept) => (
                        <TableCell key={dept}>
                          <DeptStatusCell stats={summary?.departments?.[dept]} />
                        </TableCell>
                      ))}
                      <TableCell>
                        <Badge
                          variant="secondary"
                          className={cn(
                            "font-mono text-xs uppercase",
                            order.status === "active"
                              ? "bg-emerald-50 text-emerald-700"
                              : order.status === "draft"
                              ? "bg-slate-50 text-slate-700"
                              : "bg-amber-50 text-amber-700"
                          )}
                        >
                          {order.status}
                        </Badge>
                      </TableCell>
                      {isAdmin && (
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          {isSuperAdmin ? (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive hover:text-destructive"
                                  data-testid={`delete-order-${order.order_id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Order</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete "{order.order_number}"? This will permanently delete the order and all associated tasks and documents.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    onClick={(e) => handleDeleteOrder(e, order.order_id, order.order_number)}
                                    disabled={deleting === order.order_id}
                                  >
                                    {deleting === order.order_id ? (
                                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    ) : null}
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-amber-600 hover:text-amber-700"
                              onClick={(e) => handleRequestDeletion(e, order.order_id, order.order_number)}
                              disabled={deleting === order.order_id}
                              data-testid={`request-delete-order-${order.order_id}`}
                            >
                              {deleting === order.order_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {filteredOrders.length === 0 && (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground">No orders found</p>
              <Link to="/orders/new" className="mt-4 inline-block">
                <Button variant="outline" size="sm" className="rounded-sm">
                  Create your first order
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Department Status Cell for order task summary
const DeptStatusCell = ({ stats }) => {
  if (!stats) return <span className="text-muted-foreground text-xs">-</span>;
  const total = stats.in_progress + stats.overdue + stats.pending + stats.completed;
  if (total === 0) return <span className="text-muted-foreground text-xs">-</span>;
  return (
    <div className="space-y-0.5 text-xs min-w-[100px]">
      {stats.in_progress > 0 && (
        <div className="text-amber-600 font-medium">{stats.in_progress} In Process</div>
      )}
      {stats.overdue > 0 && (
        <div className="text-rose-600 font-medium">{stats.overdue} Overdue</div>
      )}
      {stats.pending > 0 && (
        <div className="text-foreground">{stats.pending} Pending</div>
      )}
      {stats.completed > 0 && (
        <div className="text-emerald-600 font-medium">{stats.completed} Completed</div>
      )}
    </div>
  );
};

const OrdersSkeleton = () => (
  <div className="space-y-6">
    <div className="flex justify-between items-center">
      <Skeleton className="h-10 w-32" />
      <Skeleton className="h-10 w-32" />
    </div>
    <Skeleton className="h-10 w-full max-w-md" />
    <Skeleton className="h-96 w-full" />
  </div>
);
